import { alpha, colors, createTheme, responsiveFontSizes } from "@mui/material";
import _ from "lodash";
import { THEMES } from "src/constants";
import { softShadows, strongShadows } from "./shadows";
import typography from "./typography";

const remoteTheme = window["theme"];

const newGray = "#848694";
const arkaduBlue = "#0F4C81";
const arkaduRed = "#FF1744";
const arkaduRedBg = "linear-gradient(180deg, #FF1744 30%, #E6294A 90%)";
const arkaduGray =
  "linear-gradient(180deg, rgba(37,37,37,1) 0%, rgba(26,34,32,1) 40%, rgba(17,18,18,1) 100%)";

const arkaduRedDark = "#E6294A";
const arkaduGrayPallete = {
  success: "#28a745",
  danger: "#E30613",
  warning: "#F6A000",
  info: "#018198",
  primary: {
    main: "#1F2B33",
    dark: "#172026",
    light: "#FFD500",
    secondary: "#018198",
  },
  secondary: {
    main: "#018198",
    dark: "#018198",
    light: "#FEDA00",
    dark2: "#DB8905",
  },
  primaryText: "#1F2B33",
  secondaryText: "#018198",
  background: {
    default: "#1F2B33",
    secondary: "#FEDA00",
    gray: "#FFFFFF",
  },
};

const arkaduBlueLight = "#4c77b1";
const textDark = "#2d2d2d";
const arkaduBlueDark = "#002554";
const secondaryColor = "#1AFF85";
const secondaryColorLight = "#00b28e";
const secondaryColorDark = "#64ffdf";
const newGreen = "#114430";
const unicornColor = "#64ffdf";

const barAdmin = secondaryColor;

const p = arkaduGrayPallete;

const baseOptions = {
  direction: "ltr",
  typography,
  shape: {
    borderRadius: 8,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          width: "100%",
          overflowX: "hidden",
        },
        body: {
          width: "100%",
          overflowX: "hidden",
        },
        "#root": {
          width: "100%",
          overflowX: "hidden",
        },
        "*": {
          boxSizing: "border-box",
        },
        "*:focus-visible": {
          outline: `2px solid ${p.secondary.main}`,
          outlineOffset: 2,
        },
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
            scrollBehavior: "auto !important",
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            outline: `2px solid ${p.secondary.main}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 8,
          padding: "8px 20px",
          "&:hover": {
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 12px rgba(0, 0, 0, 0.35)"
                : "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
        }),
        contained: ({ theme }) => ({
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 2px 10px rgba(0, 0, 0, 0.3)"
              : "0 2px 8px rgba(0, 0, 0, 0.1)",
        }),
        outlined: ({ theme }) => ({
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.text.primary,
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.35)"
              : alpha(theme.palette.divider, 0.9),
          "&:hover": {
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.68)"
                : theme.palette.text.primary,
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.text.primary, 0.04),
          },
        }),
        text: ({ theme }) => ({
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.text.primary,
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.text.primary, 0.06),
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          color: theme.palette.text.primary,
          backgroundImage: "none",
          border: `1px solid ${alpha(
            theme.palette.divider,
            theme.palette.mode === "dark" ? 0.85 : 1,
          )}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 6px 18px rgba(3, 8, 20, 0.26)"
              : "0 2px 12px rgba(0, 0, 0, 0.08)",
          "&:hover": {
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 24px rgba(3, 8, 20, 0.30)"
                : "0 8px 24px rgba(0, 0, 0, 0.12)",
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          border: "none",
          color: theme.palette.text.primary,
          backgroundImage: "none",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 3px 14px rgba(3, 8, 20, 0.22)"
              : "0 1px 3px rgba(0, 0, 0, 0.06)",
        }),
        elevation1: ({ theme }) => ({
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 3px 14px rgba(3, 8, 20, 0.22)"
              : "0 2px 8px rgba(0, 0, 0, 0.08)",
        }),
        elevation2: ({ theme }) => ({
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 5px 18px rgba(3, 8, 20, 0.24)"
              : "0 4px 12px rgba(0, 0, 0, 0.1)",
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: 8,
          overflow: "hidden",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiTableCell-root": {
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.text.primary, 0.86)
                : alpha(theme.palette.text.primary, 0.82),
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.default, 0.36)
                : alpha(theme.palette.background.default, 0.72),
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.03)
                : "rgba(0, 0, 0, 0.02)",
          },
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.18)
                : "rgba(1, 129, 152, 0.08)",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.24)
                  : "rgba(1, 129, 152, 0.12)",
            },
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${alpha(
            theme.palette.divider,
            theme.palette.mode === "dark" ? 0.7 : 0.45,
          )}`,
          padding: "16px",
          color: theme.palette.text.primary,
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${alpha(
            theme.palette.divider,
            theme.palette.mode === "dark" ? 0.7 : 0.4,
          )}`,
          "& .MuiTabs-flexContainer": {
            gap: 8,
          },
        }),
        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9375rem",
          minHeight: 48,
          color: theme.palette.text.secondary,
          "&:hover": {
            color: p.secondary.main,
            opacity: 1,
          },
          "&.Mui-selected": {
            fontWeight: 600,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.dark, 0.94)
              : undefined,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          // background handled per-mode via background.paper token
          borderRight: "none",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: p.secondary.main,
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          margin: "2px 8px",
          color: theme.palette.text.primary,
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.12)
                : "rgba(1, 129, 152, 0.08)",
          },
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.18)
                : "rgba(1, 129, 152, 0.12)",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.24)
                  : "rgba(1, 129, 152, 0.16)",
            },
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 40,
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "10px 16px",
          "&:hover": {
            backgroundColor: "rgba(1, 129, 152, 0.08)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          overflow: "hidden",
          height: 6,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 600,
        },
      },
    },
    MUIDataTableFilter: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.default, 0.72)
              : "rgba(232, 232, 232, 0.5)",
          borderRadius: 8,
          color: theme.palette.text.primary,
        }),
      },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: theme.typography.caption.fontSize,
          fontWeight: theme.typography.fontWeightMedium,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.default, 0.45)
              : alpha(theme.palette.background.default, 0.7),
        }),
      },
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
  },
};

const themesOptions = [
  {
    name: THEMES.LIGHT,
    palette: {
      mode: "light",
      action: {
        active: p.primary.light,
      },
      background: {
        default: "#F4F6F8",
        dark: "#EBEDF0",
        paper: colors.common.white,
      },
      primary: p.primary,
      secondary: p.secondary,
      danger: {
        main: p.danger,
      },
      text: {
        primary: p.primaryText,
        secondary: p.secondaryText,
      },
      success: {
        main: p.success,
      },
      adminBar: {
        main: p.secondary,
      },
      navbar: {
        main: "rgba(255, 255, 255, 0.88)",
        dark: "#f4f6f8",
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: softShadows,
  },
  {
    name: THEMES.DARK,
    palette: {
      mode: "dark",
      divider: "rgba(148, 163, 184, 0.24)",
      action: {
        active: "rgba(255, 255, 255, 0.68)",
        hover: "rgba(255, 255, 255, 0.06)",
        selected: "rgba(255, 255, 255, 0.12)",
        disabled: "rgba(255, 255, 255, 0.30)",
        disabledBackground: "rgba(255, 255, 255, 0.14)",
        focus: "rgba(255, 255, 255, 0.14)",
      },
      background: {
        default: "#24252b",
        dark: "#1f2025",
        paper: "#2b2d34",
      },
      primary: {
        main: "#8a85ff",
      },
      success: {
        main: p.success,
      },
      danger: {
        main: p.danger,
      },
      secondary: {
        main: "#8a85ff",
      },
      text: {
        primary: "rgba(255, 255, 255, 0.95)",
        secondary: "rgba(255, 255, 255, 0.78)",
        disabled: "rgba(255, 255, 255, 0.44)",
      },
      adminBar: {
        main: p.secondary,
      },
      navbar: {
        main: "#2b2d34",
        dark: "#24252b",
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: strongShadows,
  },
  // {
  //   name: THEMES.UNICORN,
  //   palette: {
  //     mode: "dark",
  //     action: {
  //       active: "rgba(255, 255, 255, 0.54)",
  //       hover: "rgba(255, 255, 255, 0.04)",
  //       selected: "rgba(255, 255, 255, 0.08)",
  //       disabled: "rgba(255, 255, 255, 0.26)",
  //       disabledBackground: "rgba(255, 255, 255, 0.12)",
  //       focus: "rgba(255, 255, 255, 0.12)",
  //     },
  //     background: {
  //       default: "#2a2d3d",
  //       dark: "#222431",
  //       paper: "#2a2d3d",
  //     },
  //     primary: {
  //       main: unicornColor,
  //     },
  //     danger: {
  //       main: p.danger,
  //     },
  //     success: {
  //       main: p.success,
  //     },
  //     secondary: {
  //       main: unicornColor,
  //     },
  //     text: {
  //       primary: "#f6f5f8 ",
  //       secondary: "#9699a4",
  //     },
  //     adminBar: {
  //       main: p.secondary,
  //     },
  //   },
  //   shape: {
  //     borderRadius: 12,
  //   },
  //   shadows: strongShadows,
  // },
];

const LEGACY_THEME_TO_MODE = {
  ONE_DARK: THEMES.DARK,
  UNICORN: THEMES.DARK,
};

const normalizeThemeMode = (themeMode) => {
  if (themeMode === THEMES.LIGHT || themeMode === THEMES.DARK) {
    return themeMode;
  }
  return LEGACY_THEME_TO_MODE[themeMode] || THEMES.LIGHT;
};

/**
 * Elimina recursivamente los sufijos `!important` de todos los valores string.
 * Los temas de cliente heredados (MUI v4/v5) usaban !important que ahora
 * bloquea los overrides del tema base.
 */
const stripImportant = (value) => {
  if (typeof value === "string") {
    return value.replace(/\s*!important\s*$/i, "").trim();
  }
  if (Array.isArray(value)) return value.map(stripImportant);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, stripImportant(v)]),
    );
  }
  return value;
};

/**
 * Envuelve una definición de slot de styleOverrides para que solo se aplique
 * en light mode. En dark mode devuelve {} para no interferir con los overrides
 * del tema base.
 *
 * NOTA: las funciones NO se envuelven — si el cliente ya provee
 * ({ theme }) => ..., es responsable de manejar ambos modos. Solo los objetos
 * planos (herencia legacy MUI v4/v5) se convierten a light-mode-only.
 */
const wrapLightOnly = (slotDef) => {
  if (typeof slotDef === "function") {
    return slotDef;
  }
  if (slotDef && typeof slotDef === "object") {
    const safe = stripImportant(slotDef);
    return ({ theme }) => (theme?.palette?.mode === "dark" ? {} : safe);
  }
  return slotDef;
};

/**
 * Normaliza el tema remoto (cliente) para compatibilidad con dark mode:
 * - Elimina !important de todos los valores CSS
 * - Convierte los styleOverrides de componentes en funciones light-mode-only
 *
 * Los temas de cliente deben eventualmente migrarse a funciones ({ theme }) => ({})
 * para tener soporte nativo dark/light. Esta utilidad sirve como capa de
 * compatibilidad mientras se actualizan.
 */
const normalizeRemoteTheme = (remote) => {
  if (!remote || typeof remote !== "object") return {};
  const result = { ...remote };
  if (result.components) {
    const components = {};
    for (const [name, def] of Object.entries(result.components)) {
      if (!def?.styleOverrides) {
        components[name] = def;
        continue;
      }
      const styleOverrides = {};
      for (const [slot, slotDef] of Object.entries(def.styleOverrides)) {
        styleOverrides[slot] = wrapLightOnly(slotDef);
      }
      components[name] = { ...def, styleOverrides };
    }
    result.components = components;
  }
  return result;
};

const normalizedRemoteTheme = normalizeRemoteTheme(remoteTheme);

/**
 * Evita que tokens típicamente "light" del tema remoto rompan el dark mode.
 * Conserva colores de marca (primary/secondary/navbar, etc.) pero descarta
 * background/text/mode/type cuando el modo activo es dark.
 */
const getRemoteThemeForMode = (remote, mode) => {
  if (!remote || typeof remote !== "object") return {};

  if (mode !== THEMES.DARK) {
    return remote;
  }

  const scoped = _.cloneDeep(remote);
  if (!scoped.palette || typeof scoped.palette !== "object") {
    return scoped;
  }

  delete scoped.palette.background;
  delete scoped.palette.text;
  delete scoped.palette.mode;
  delete scoped.palette.type;

  return scoped;
};

const scaleTypography = (typographyConfig, scale = 1) => {
  const next = { ...typographyConfig };
  Object.keys(next).forEach((key) => {
    if (next[key] && typeof next[key] === "object" && next[key].fontSize) {
      next[key] = {
        ...next[key],
        fontSize: Math.round(next[key].fontSize * scale),
      };
    }
  });
  return next;
};

export const buildTheme = (config = {}, customerTheme = {}) => {
  const themeMode = normalizeThemeMode(config.theme);
  const textScale = [1, 1.125, 1.25].includes(parseFloat(config.textScale))
    ? parseFloat(config.textScale)
    : 1;

  let themeOptions = themesOptions.find((theme) => theme.name === themeMode);

  if (!themeOptions) {
    themeOptions = themesOptions.find((theme) => theme.name === THEMES.LIGHT);
    [themeOptions] = themesOptions;
  }

  themeOptions = _.cloneDeep(themeOptions);

  if (customerTheme.primary) {
    themeOptions = _.merge(themeOptions, { palette: customerTheme });
  }

  const baseOptionsForMerge = {
    ...baseOptions,
    typography,
  };

  // 1. Normalizar el tema cliente: elimina !important y envuelve overrides como light-only
  const normalizedRemote = getRemoteThemeForMode(
    normalizedRemoteTheme,
    themeMode,
  );

  // 2. Aplicar el tema cliente normalizado (palette + typography + shape + components del cliente)
  themeOptions = _.merge(themeOptions, normalizedRemote);

  // 3. Aplicar SOLO los components base al final → preservan dark mode en MUI.
  //    typography y shape del cliente ganan (base actúa como fallback, no override).
  themeOptions = _.merge(themeOptions, {
    components: baseOptionsForMerge.components,
  });
  // Typography: base como fallback en variantes que el cliente no definió
  themeOptions = {
    ...themeOptions,
    typography: _.merge(
      {},
      baseOptionsForMerge.typography,
      themeOptions.typography,
    ),
  };
  // Aplicar escala final a TODA la tipografía efectiva (base + remota)
  themeOptions = {
    ...themeOptions,
    typography: scaleTypography(themeOptions.typography, textScale),
  };
  // shape: el cliente define borderRadius; si no definió, conservar el base
  if (!normalizedRemote.shape?.borderRadius) {
    themeOptions = {
      ...themeOptions,
      shape: { ...baseOptionsForMerge.shape, ...(themeOptions.shape || {}) },
    };
  }

  var themeConfig = {
    ...themeOptions,
    direction: config.direction,
    components: {
      ...themeOptions.components,
      MuiCssBaseline: {
        ...themeOptions.components?.MuiCssBaseline,
        styleOverrides: {
          ...(themeOptions.components?.MuiCssBaseline?.styleOverrides || {}),
          html: {
            fontSize: `${Math.round(100 * textScale)}%`,
          },
        },
      },
    },
  };

  // En dark mode, forzamos colores de texto base legibles incluso si el
  // tema remoto del cliente trae overrides de palette.text con bajo contraste.
  if (themeMode === THEMES.DARK) {
    themeConfig = {
      ...themeConfig,
      palette: {
        ...themeConfig.palette,
        text: {
          ...(themeConfig.palette?.text || {}),
          primary: "rgba(255, 255, 255, 0.95)",
          secondary: "rgba(255, 255, 255, 0.78)",
          disabled: "rgba(255, 255, 255, 0.42)",
        },
      },
    };
  }

  let theme = createTheme(themeConfig);

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
